<script lang="ts" setup>
import icFilter from '@/assets/img/icon/ic-filter.png';
import { AppButton, AppTypography } from '@/components/base';
import { NoListingCard } from '@/components/support-seeker';
import JobForm from '@/components/support-seeker/forms/JobForm.vue';
import { SupportWorkerCard } from '@/components/support-worker';
import { useAuthStore } from '@/modules/auth/store/auth';

const props = withDefaults(
  defineProps<{
    title?: string;
    subtitle?: string;
    
    limit?: number;
    isFilter?: boolean;
    recommended?: boolean;
  }>(),
  {
    title: 'Browse workers',
    subtitle: 'You can invite them to apply',

    limit: 12,
    isFilter: true,
    recommended: false,
  }
);

const page = ref(1);
const showVerification = ref(false);
const { isFullyVerified } = useAuthStore();

const { supportWorkers, supportWorkersMeta, fetching, getSupportWorkers } = useSupportWorker();

const emit = defineEmits<{
  (e: 'handleJobForm', value: boolean): void;
}>();

const fetch = async (newPage: number = 1): Promise<void> => {
  page.value = newPage;
  await getSupportWorkers(page.value, props.limit, props.recommended);
};
const showJobForm = ref(false);
const handleJobForm = (bool: boolean): void => {
  emit('handleJobForm', bool);
};

const loadMore = (): void => {
  const nextPage = page.value + 1;
  if (nextPage <= supportWorkersMeta.value.last_page) {
    fetch(nextPage);
  }
};

const toggleFilter = (): void => {
  if (isFullyVerified) {
    toggleVerification(true);
  }
};

const toggleVerification = (bool?: boolean): void => {
  showVerification.value = bool === undefined ? !showVerification.value : bool;
};

// Add refresh method for external calls
const refreshData = async (): Promise<void> => {
  await fetch(1);
};

// Expose the refresh method
defineExpose({
  refreshData
});

onMounted(() => {
  nextTick(() => {
    fetch();
    // if (isFullyVerified) {
    // }
  });
});
</script>
<template>
  <div v-if="!showJobForm" class="support-worker-list">
    <div class="support-worker-list__header d-flex justify-space-between mb-6">
      <div>
        <AppTypography type="headline-1" class="mb-1">{{ title }}</AppTypography>
        <AppTypography type="subheadline-2">{{ subtitle }}</AppTypography>
      </div>

      <AppButton v-if="isFilter" size="small" color="#000" class="support-worker-list__filter" @click="toggleFilter">
        <v-img width="24" height="24" :src="icFilter" />
      </AppButton>
    </div>
    <NoListingCard @handleJobForm="handleJobForm" v-if="supportWorkers.length > 0 && !fetching" />
    <div class="support-worker-list__list mt-3">
      <v-row v-if="supportWorkers.length > 0" no-gutter>
        <v-col v-for="(supportWorker, index) in supportWorkers" :key="index" cols="12" md="4">
          <SupportWorkerCard
            :avatar="
              supportWorker.avatar?.thumb_url ||
              supportWorker.avatar_permanent_thumb_url ||
              supportWorker.avatar?.url ||
              supportWorker.avatar_permanent_url
            "
            :firstName="supportWorker.first_name"
            :middleName="supportWorker.middle_name"
            :lastName="supportWorker.last_name"
            :workExperiences="supportWorker.work_experiences"
            :services-offered="supportWorker.services_offered"
            :qualifications_needed="supportWorker.qualifications"
            :availability="supportWorker.availability_schedule"
            :workerListings="supportWorkers"
            :workerId="supportWorker.id"
            :is-fully-verified="isFullyVerified"
          />
        </v-col>
      </v-row>

      <!-- No Data Found State -->
      <template v-if="supportWorkers.length === 0 && !fetching">
        <div class="no-data-container text-center py-12">
          <v-icon size="80" color="grey-lighten-1" class="mb-4">mdi-account-search-outline</v-icon>
          <h3 class="text-h5 font-weight-medium mb-3 text-high-emphasis">No support workers found</h3>
          <p class="text-body-1 text-medium-emphasis mb-6" style="max-width: 400px; margin: 0 auto;">
            We couldn't find any support workers in your area at the moment. Try adjusting your location or check back later.
          </p>
          <!-- <AppButton 
            color="primary" 
            variant="outlined"
            @click="fetch(1)"
            prepend-icon="mdi-refresh"
            class="text-none"
          >
            Refresh
          </AppButton> -->
        </div>
      </template>

      <template v-if="fetching">
        <v-row no-gutter>
          <v-col v-for="n in 3" :index="n" cols="12" md="4">
            <div class="support-worker-list__list__loader">
              <v-skeleton-loader type="list-item-avatar" />
              <v-skeleton-loader type="list-item-three-line" />
              <v-skeleton-loader type="heading" />
            </div>
          </v-col>
        </v-row>
      </template>

      <div v-intersect="loadMore" />
    </div>
  </div>
  <JobForm v-if="showJobForm" v-model="showJobForm" :job-listing="{}" />
  <ComplateProfileDialog v-model="showVerification" />
</template>
<style lang="scss" scoped>
.support-worker-list {
  &__filter {
    width: 40px;
    height: 40px;
    min-width: inherit;
    border-radius: 8px !important;
  }

  &__list {
    &__loader {
      border-radius: 16px;
      box-shadow: 0px 8px 9px 0px #0000001a;
      overflow: hidden;
    }
  }
}
</style>
